// Smart Daara - School-scoped Landing + Login page
// Rendered at /<tenantSlug>. Shows a mini landing for the school (logo, name, description)
// with an inline login form. Redirects to the tenant dashboard on success.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { tenantAPI } from '../lib/api';
import { getDashboardPath } from '../lib/routing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Lock, Eye, EyeOff, Globe, ArrowLeft, Loader2, School } from 'lucide-react';
import { toast } from 'sonner';
import SchoolNotFoundPage from './SchoolNotFoundPage';

const DEFAULT_LOGO = '/logo.png';

export default function SchoolLoginPage() {
  const { tenantSlug } = useParams();
  const { login, isAuthenticated, user } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantNotFound, setTenantNotFound] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Resolve tenant by slug
  useEffect(() => {
    let cancelled = false;
    setTenantLoading(true);
    setTenantNotFound(false);
    tenantAPI.getBySlug(tenantSlug)
      .then(res => { if (!cancelled) setTenant(res.data); })
      .catch(() => { if (!cancelled) setTenantNotFound(true); })
      .finally(() => { if (!cancelled) setTenantLoading(false); });
    return () => { cancelled = true; };
  }, [tenantSlug]);

  // If user is already authenticated for this tenant, redirect to their dashboard
  useEffect(() => {
    if (isAuthenticated && user && !tenantLoading && tenant) {
      if (user.tenant_slug === tenantSlug || user.role === 'super_admin') {
        navigate(getDashboardPath(user), { replace: true });
      }
    }
  }, [isAuthenticated, user, tenant, tenantLoading, tenantSlug, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedUser = await login(email, password);
      // Verify the user belongs to this tenant (or is super_admin)
      if (loggedUser.role !== 'super_admin' && loggedUser.tenant_slug !== tenantSlug) {
        toast.error(
          language === 'fr'
            ? "Ce compte n'appartient pas à cette école."
            : 'This account does not belong to this school.'
        );
        setLoading(false);
        return;
      }
      toast.success(language === 'fr' ? 'Connexion réussie!' : 'Login successful!');
      navigate(getDashboardPath(loggedUser), { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail ||
        (language === 'fr' ? 'Erreur de connexion' : 'Login failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (tenantLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // 404 for invalid slug
  if (tenantNotFound || !tenant) {
    return <SchoolNotFoundPage slug={tenantSlug} />;
  }

  const schoolName = tenant.name;
  const schoolDescription =
    (language === 'fr' ? (tenant.description_fr || tenant.description) : (tenant.description || tenant.description_fr))
    || (language === 'fr'
      ? "Bienvenue sur la plateforme d'apprentissage de votre école."
      : 'Welcome to your school learning platform.');

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50/30 flex flex-col"
      data-testid="school-login-page"
    >
      {/* Header */}
      <header className="p-4 flex justify-between items-center max-w-7xl mx-auto w-full">
        <Link to="/home" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'fr' ? 'Smart Daara' : 'Smart Daara'}</span>
        </Link>
        <Button variant="ghost" size="sm" onClick={toggleLanguage} className="flex items-center gap-1">
          <Globe className="w-4 h-4" />
          {language.toUpperCase()}
        </Button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">

          {/* Left: school landing */}
          <div className="space-y-6 text-center md:text-left" data-testid="school-landing-block">
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-md flex items-center justify-center overflow-hidden">
                <img
                  src={tenant.logo_url || DEFAULT_LOGO}
                  alt={schoolName}
                  className="w-full h-full object-contain p-2"
                  onError={(e) => { e.currentTarget.src = DEFAULT_LOGO; }}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <School className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider font-semibold">
                    {language === 'fr' ? 'Espace École' : 'School Space'}
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-outfit font-bold text-slate-900" data-testid="school-name">
                  {schoolName}
                </h1>
              </div>
            </div>
            <p className="text-slate-600 text-base leading-relaxed" data-testid="school-description">
              {schoolDescription}
            </p>
            <div className="pt-2 flex flex-wrap gap-3 justify-center md:justify-start text-sm">
              {tenant.contact_email && (
                <a
                  href={`mailto:${tenant.contact_email}`}
                  className="flex items-center gap-1 text-slate-600 hover:text-emerald-700"
                >
                  <Mail className="w-4 h-4" /> {tenant.contact_email}
                </a>
              )}
              {tenant.contact_phone && (
                <span className="text-slate-500">· {tenant.contact_phone}</span>
              )}
            </div>
          </div>

          {/* Right: login card */}
          <Card className="w-full shadow-xl border-0" data-testid="school-login-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-outfit">
                {language === 'fr' ? 'Connexion' : 'Sign in'}
              </CardTitle>
              <CardDescription>
                {language === 'fr'
                  ? `Accédez à votre espace ${schoolName}`
                  : `Access your ${schoolName} space`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="school-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="school-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  disabled={loading}
                  data-testid="school-login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('loading')}
                    </span>
                  ) : (
                    t('login')
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-xs text-slate-500">
                  {language === 'fr'
                    ? "Pas de compte ? Contactez l'administrateur de votre école."
                    : 'No account? Contact your school administrator.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-xs text-slate-500">
        {language === 'fr' ? 'Propulsé par' : 'Powered by'}{' '}
        <Link to="/home" className="text-emerald-700 hover:underline font-medium">Smart Daara</Link>
      </footer>
    </div>
  );
}
